import React, { useEffect, useMemo, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { Badge, Button, message, Select, Tag, Upload } from "antd";
import {
  ExportOutlined,
  HomeOutlined,
  PlusCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router";
import Table from "../../Common/Table";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import { RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import {
  useUsersTableGet,
  UserTableRow,
  useUserGetById,
} from "../../../hooks/api/users";
import {
  queryFilterParams,
  getSorterParams,
  getUserRequestPath,
  formatDefaultTime,
} from "../../../utils";
import VaadCreateModal from "./Create";
import {
  useVaadTableGet,
  VaadGetParams,
  VaadTableRow,
} from "../../../hooks/api/vaads";
import { LazySelect } from "../../Common/SelectLazyLoader";
import { useVaadTabs } from "./SinglePage/VaadTabs/context";
import { useAuth, UserRoleType } from "../../../store/auth";
import { useMeetingExport } from "../../../hooks/api/meetings";
import ImportMeetingModal from "./ImportMeeting";
import { IconExport } from "../../Common/Icon";

const VaadStatusDisplay = {
  active: { text: "Active", status: "Success" },
  inactive: { text: "Inactive", status: "Error" },
  not_started: { text: "Not started", status: "Warning" },
} as const;

const getLocationName = (value: unknown): string => {
  if (value && typeof value === "object" && "name" in value) {
    return String((value as Record<string, unknown>).name ?? "");
  }
  return typeof value === "string" ? value : "";
};

const getVaadStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "green";
    case "inactive":
      return "red";
    case "pending":
      return "orange";
    default:
      return "gray";
  }
};

function ToolBar({
  onCreate,
  setTableKey,
  onImportOpen,
  onExport,
  exportLoading,
}: {
  onCreate: () => void;
  setTableKey: () => void;
  onImportOpen: () => void;
  onExport: () => void;
  exportLoading: boolean;
}) {
  const { user } = useAuth();

  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  return [
    ...(isAdmin
      ? [
        <Button
          key="create"
          icon={<PlusCircleOutlined />}
          type="primary"
          onClick={(e) => {
            e.preventDefault();
            onCreate();
          }}
        >
          Add Vaad
        </Button>,
      ]
      : []),
    <Button
      key="import"
      icon={<UploadOutlined />}
      type="primary"
      ghost
      onClick={onImportOpen}
    >
      Import Meeting
    </Button>,
    <Button
      key="export"
      icon={<IconExport />}
      type="primary"
      ghost
      loading={exportLoading}
      onClick={onExport}
    >
      Export Meetings
    </Button>,
  ];
}

export default function VaadimListContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setActiveTab } = useVaadTabs();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<VaadTableRow | null>(
    null,
  );
  const vaadTableGet = useVaadTableGet();
  const meetingExport = useMeetingExport();

  const handleExportMeetings = async () => {
    try {
      const blob = await meetingExport.fetch();
      if (!(blob instanceof Blob)) {
        throw new Error("Invalid export response");
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "upcoming-meetings.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      message.error("Failed to export meetings");
    }
  };

  const userIdFromUrl = searchParams.get("userId");
  const userGetById = useUserGetById(userIdFromUrl || undefined);

  const leaderInitialOptions = useMemo(() => {
    console.log("userIdFromUrl:", userIdFromUrl);
    console.log("userGetById.data:", userGetById.data);

    if (!userIdFromUrl || !userGetById.data) return undefined;

    const fullName = userGetById.data.fullName;
    console.log("fullName:", fullName);

    return [
      {
        label: fullName || "Unknown",
        value: userIdFromUrl,
      },
    ];
  }, [userIdFromUrl, userGetById.data]);

  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  const handleEdit = (record: VaadTableRow) => {
    setSelectedRecord(record);
    setEditOpen(true);
  };
  const columns = (t: TFunction): ProColumns<VaadTableRow>[] => [
    {
      title: "Vaad Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      hideInSearch: true,
    },
    {
      title: "Vaad ID",
      dataIndex: "vaadId",
      key: "vaadId",
      sorter: true,
      hideInSearch: true,
    },
    {
      title: "Vaad Leader",
      dataIndex: "user",
      key: "user",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        const user = record.user;
        if (!user) return "-";
        const { firstName, lastName } = user;
        return `${firstName || ""} ${lastName || ""}`.trim() || "-";
      },
    },
    {
      title: "User count",
      dataIndex: "userCount",
      key: "userCount",
      sorter: true,
      hideInSearch: true,
    },
    {
      title: "Day",
      dataIndex: "defaultDay",
      key: "defaultDay",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <span className="capitalize-first-letter">{record.defaultDay}</span>
      ),
    },
    {
      title: "Time",
      dataIndex: "defaultTime",
      key: "time",
      sorter: false,
      hideInSearch: true,
      align: "center",
      render: (_, record) => formatDefaultTime(record.defaultTime) || "-",
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        const date = dayjs(record.createdAt);
        return date.isValid() ? date.format("MMMM D, YYYY") : "-";
      },
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      sorter: false,
      hideInSearch: true,
      render: (_, record) => getLocationName(record.city) || "-",
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      sorter: false,
      hideInSearch: true,
      render: (_, record) => getLocationName(record.state) || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      hideInSearch: true,
      valueEnum: VaadStatusDisplay,
      render: (_, record) => (
        <Badge
          style={{ whiteSpace: "nowrap" }}
          color={getVaadStatusColor(record.status)}
          text={
            <span className="capitalize-first-letter">
              {record.status.replace(/_/g, " ")}
            </span>
          }
        />
      ),
    },
    {
      title: "Vaad Leader",
      dataIndex: "user",
      key: "vaadLeaderSearch",
      hideInTable: true,
      valueType: "select",
      renderFormItem: (_, props) => {
        const initialOpts = leaderInitialOptions;

        return (
          <LazySelect
            key={`lazy-${userIdFromUrl}-${userGetById.data?.fullName || "loading"}`}
            {...props}
            initialOptions={initialOpts}
            fetchOptions={async ({ search, page, pageSize }) => {
              const res = await vaadTableGet.fetch({
                page,
                pageSize,
                search,
              });
              return (res?.data || []).filter((item) => item.user?.id);
            }}
            mapOption={(item) => ({
              label: [item.user?.firstName, item.user?.lastName]
                .filter(Boolean)
                .join(" "),
              value: item.user!.id,
            })}
          />
        );
      },

      search: {
        transform: (value) => ({
          userId: value,
        }),
      },

      render: (_, record) => (
        <div>
          {record.user?.firstName} {record.user?.lastName}
        </div>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      hideInTable: true,
      valueType: "dateRange",
      fieldProps: { format: "MM-DD-YYYY" },
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            createdAtFrom: value[0],
            createdAtTo: value[1],
          };
        },
      },
    },
    {
      title: "City",
      dataIndex: "city",
      key: "citySearch",
      hideInTable: true,
      valueType: "select",
      renderFormItem: (_, props) => (
        <LazySelect
          {...props}
          fetchOptions={async ({ search, page, pageSize }) => {
            const res = await vaadTableGet.fetch({
              page,
              pageSize,
              search,
            });
            return (res?.data || []).filter((item) =>
              getLocationName(item.city),
            );
          }}
          mapOption={(item) => {
            const name = getLocationName(item.city);
            return {
              label: name,
              value: name,
            };
          }}
        />
      ),

      search: {
        transform: (value) => ({
          city: value,
        }),
      },
    },
    {
      title: "State",
      dataIndex: "state",
      key: "stateSearch",
      hideInTable: true,
      valueType: "select",
      renderFormItem: (_, props) => (
        <LazySelect
          {...props}
          fetchOptions={async ({ search, page, pageSize }) => {
            const res = await vaadTableGet.fetch({
              page,
              pageSize,
              search,
            });
            return (res?.data || []).filter((item) =>
              getLocationName(item.state),
            );
          }}
          mapOption={(item) => {
            const name = getLocationName(item.state);
            return {
              label: name,
              value: name,
            };
          }}
        />
      ),

      search: {
        transform: (value) => ({
          state: value,
        }),
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      hideInTable: true,
      valueEnum: VaadStatusDisplay,
      render: (_, record) => (
        <Badge
          color={getVaadStatusColor(record.status)}
          text={
            <span className="capitalize-first-letter">
              {" "}
              {record.status.replace(/_/g, " ")}
            </span>
          }
        />
      ),
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<VaadTableRow>>> => {
    const newParams: VaadGetParams = {
      orderByColumn: "createdAt",
      page: current || 1,
      pageSize: pageSize || 10,
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return vaadTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };
  const actions: [
    string,
    (props: { text: React.ReactNode; record: VaadTableRow; id: number }) => any,
    (props: {
      text: React.ReactNode;
      record: VaadTableRow;
      id: number;
    }) => boolean,
  ][] = [
      [
        "edit",
        ({
          record,
        }: {
          text: React.ReactNode;
          record: VaadTableRow;
          id: number;
        }) => ({
          title: "Edit vaad",
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
            handleEdit(record);
          },
        }),
        () => true,
      ],
    ];

  return (
    <>
      <Table<VaadTableRow>
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle={t("pages.users.tableName")}
        toolBarRender={() => [
          <ToolBar
            key="toolbar"
            onCreate={() => setCreateOpen(true)}
            setTableKey={() => setTableKey(Date.now())}
            onImportOpen={() => setImportOpen(true)}
            onExport={handleExportMeetings}
            exportLoading={meetingExport.loading}
          />,
        ]}
        request={tableRequest}
        columns={columns(t)}
        onReset={() => {
          setSearchParams({}, { replace: true });
          setTableKey(Date.now());
        }}
        onRow={(record) => ({
          onClick: () => {
            setActiveTab("members");
            navigate(`${record.id}/view`);
          },
          style: { cursor: "pointer" },
        })}
        // actions={[
        //   [
        //     "edit",
        //     ({ record }) => ({
        //       title: "Edit vaad",
        //       onClick: (e: MouseEvent) => {
        //         e.stopPropagation();
        //         handleEdit(record);
        //       },
        //     }),
        //   ],
        // ]}
        {...(isAdmin ? { actions } : {})}
        search={{
          style: {
            // display: 'none',
          },
        }}
        options={{
          search: {
            name: "search",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-vaadim",
          persistenceType: "localStorage",
        }}
      />
      <VaadCreateModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        setTableKey={() => setTableKey(Date.now())}
      />
      <VaadCreateModal
        open={editOpen}
        mode="edit"
        onClose={() => {
          setEditOpen(false);
          setSelectedRecord(null);
        }}
        setTableKey={() => setTableKey(Date.now())}
        initialState={selectedRecord}
      />
      <ImportMeetingModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
    </>
  );
}
